import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { TestSuiteDto } from 'src/app/models/dto/testSuiteDto';
import { formField } from 'src/app/models/formField';
import { ApolloClientService } from './apollo-client.service';
import { GraphQLBaseTestItemOutput } from 'src/app/graphQL/models/output/graphql-base-test-item-output';
import { GETALL_TEST_SUITES_QUERY, GET_TEST_SUITE_QUERY, GET_TEST_SUITE_SEARCH_UI_TEMPLATE_QUERY, GET_TEST_SUITE_UI_TEMPLATE_QUERY, SEARCH_TEST_SUITE_QUERY, GET_EXPORTED_TEST_SUITE_VERSION_QUERY } from 'src/app/graphQL/queries/test-suite-queries';
import { convertGraphqlBaseTestItemToDto, convertGraphqlFormFieldToDto, convertGraphqlResponseToDto, convertGraphqlResponseToDtoSimpleReturnValue } from 'src/app/graphQL/mappers/common.mappers';
import { GraphQLResponseOutput } from 'src/app/graphQL/models/output/graphql-response-output';
import { GraphQLFormFieldOutput } from 'src/app/graphQL/models/output/graphql-form-field-output';
import { convertTestSuiteDtoToGraphqlInput } from 'src/app/graphQL/mappers/test-suite.mapper';
import { CREATE_TEST_SUITE_MUTATION, DELETE_TEST_SUITE_MUTATION, EXPORT_TEST_SUITE_MUTATION, UPDATE_TEST_SUITE_MUTATION } from 'src/app/graphQL/mutations/test-suite-mutations';
import { DualApiService, Scope } from '../migration/dual-api.service';

@Injectable({
  providedIn: 'root'
})
export class TestSuiteApiService {

  constructor(private apolloClientService: ApolloClientService, private dualApiService: DualApiService) { }

  getAll(company: string, regBy: string, functionalGroupId?: string): Observable<BaseTestItemDto[]> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorAllTestSuites : GraphQLBaseTestItemOutput[]}>({
          query: GETALL_TEST_SUITES_QUERY,
          variables: {Company: company, RegBy: regBy, OrgGroup: functionalGroupId}
        })
      ),
      map(results => {
        const testSuites = results.data.configuratorAllTestSuites
        if (!testSuites)
          return undefined

        return testSuites.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    let scope: Scope | undefined;
    if (regBy) scope = 'MINE';
    else if (functionalGroupId) scope = 'MY_GROUP';

    return this.dualApiService.dualGetAll('testSuites', [], oldApiCall$, scope);
  }

  getByUid(uid: string): Observable<ResponseDto> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestSuiteById: GraphQLResponseOutput}>({
          query: GET_TEST_SUITE_QUERY,
          variables: {Uid: uid}
        })
      ),
      map(results => {
        const testSuite = results.data.configuratorTestSuiteById
        if (!testSuite)
          return undefined

        return convertGraphqlResponseToDto<TestSuiteDto>(testSuite);
      })
    );

    return this.dualApiService.dualGetByUid('testSuites', uid, oldApiCall$);
  }

  getUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client => 
        client.query<{configuratorTestSuiteUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_TEST_SUITE_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const testSuiteUiTemplate = results.data.configuratorTestSuiteUiTemplate
        if (!testSuiteUiTemplate)
          return undefined

        return testSuiteUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  getSearchUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client => 
        client.query<{configuratorTestSuiteSearchUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_TEST_SUITE_SEARCH_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const testSuiteUiTemplate = results.data.configuratorTestSuiteSearchUiTemplate

        if (!testSuiteUiTemplate)
          return undefined

        return testSuiteUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  create(testSuite: TestSuiteDto): Observable<ResponseDto> {
    const graphqlTestSuiteInput = convertTestSuiteDtoToGraphqlInput(testSuite)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorCreateTestSuite: GraphQLResponseOutput}>({
          mutation: CREATE_TEST_SUITE_MUTATION,
          variables: {TestSuite: graphqlTestSuiteInput }
        })
      ),
      map(result => convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorCreateTestSuite as GraphQLResponseOutput))
    );

    return this.dualApiService.dualCreate('testSuites', testSuite, oldApiCall$);
  }

  update(testSuite: TestSuiteDto): Observable<ResponseDto> {
    const graphqlTestSuiteInput = convertTestSuiteDtoToGraphqlInput(testSuite)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorUpdateTestSuite: GraphQLResponseOutput}>({
          mutation: UPDATE_TEST_SUITE_MUTATION,
          variables: {TestSuite: graphqlTestSuiteInput }
        })
      ),
      map(result => convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorUpdateTestSuite as GraphQLResponseOutput))
    );

    return this.dualApiService.dualUpdate('testSuites', testSuite.uid, testSuite, oldApiCall$);
  }

  delete(uid: string, regBy: string): Observable<any> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DELETE_TEST_SUITE_MUTATION,
          variables: {Uid: uid}
        })
      ),
      map(result =>(result.data))
    );

    return this.dualApiService.dualDelete('testSuites', uid, oldApiCall$);
  }

  exportTestSuite(uid: string): Observable<boolean> {//To external
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorExportTestSuite: boolean}>({
          mutation: EXPORT_TEST_SUITE_MUTATION,
          variables: { Uid: uid }
        })
      ),
      map(result => result.data.configuratorExportTestSuite)
    )
  }

  search(testSuite: TestSuiteDto, scope?: Scope): Observable<BaseTestItemDto[]> {
    const graphqlTestSuiteInput = convertTestSuiteDtoToGraphqlInput(testSuite);

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSearchTestSuite: GraphQLBaseTestItemOutput[]}>({
          query: SEARCH_TEST_SUITE_QUERY,
          variables: {TestSuite: graphqlTestSuiteInput }
        })
      ),
      map(results => {
        const testSuites = results?.data?.configuratorSearchTestSuite
        if (!testSuites)
          return undefined;

        return testSuites.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    return this.dualApiService.dualSearch('testSuites', testSuite, oldApiCall$, scope);
  }

  getExportedTestSuiteVersion(uid: string): Observable<number | null> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorGetExportedTestSuiteVersion: number | null}>({
          query: GET_EXPORTED_TEST_SUITE_VERSION_QUERY,
          variables: { Uid: uid }
        })
      ),
      map(result => result.data.configuratorGetExportedTestSuiteVersion)
    )
  }
}
